import { useCallback, useState } from "react";
import {
  useStartNewReportJobMutation,
  useLazyGetNewReportJobStatusQuery,
  useEnsureAccountHistoryForReportMutation,
  useGetStrategyByIdQuery,
} from "features/trading/accounts";
import { ReportJobStatus } from "constants/index";
import {
  NewReportRequest,
  NewReportResponse,
  optionTypeFromCallOrPut,
} from "helpers/types";
import { extractReportErrorMessage } from "./extractReportErrorMessage";
import { AlertColor } from "@mui/material";
import { useAccountHistoryStatus } from "./useAccountHistoryStatus";
import {
  pollReportJob,
  reportJobErrorMessage,
} from "./reportJobPolling";

type UseReportCalculationOptions = {
  fallbackErrorMessage: string;
  accountId: number | null;
  strategyId: number | null;
};

type ReportData = NewReportResponse["data"];

type HistoryNotice = {
  status: AlertColor;
  message: string;
};

export const useReportCalculation = ({
  fallbackErrorMessage,
  accountId,
  strategyId,
}: UseReportCalculationOptions) => {
  const [startNewReportJob] = useStartNewReportJobMutation();
  const [fetchJobStatus] = useLazyGetNewReportJobStatusQuery();
  const [ensureHistory, { isLoading: isEnsuringHistory }] =
    useEnsureAccountHistoryForReportMutation();
  const [isCalculating, setIsCalculating] = useState(false);
  const { data: strategy } = useGetStrategyByIdQuery(strategyId as number, {
    skip: !strategyId,
  });
  const assetId = strategy?.asset ?? null;
  const optionType = strategy
    ? optionTypeFromCallOrPut(strategy.call_or_put)
    : null;
  const { hasBoth } = useAccountHistoryStatus(accountId, assetId, optionType);
  const [error, setError] = useState("");
  const [historyNotice, setHistoryNotice] = useState<HistoryNotice | null>(
    null,
  );

  const calculate = useCallback(
    async (args: NewReportRequest): Promise<ReportData | null> => {
      setHistoryNotice(null);

      // skip when both already exist
      if (!hasBoth) {
        if (!assetId || !optionType) {
          setHistoryNotice({
            status: "error",
            message:
              "Couldn't resolve asset for the selected strategy. Please try again.",
          });
          return null;
        }
        try {
          const { data: created } = await ensureHistory({
            orion_account_id: args.orion_account_id,
            asset_id: assetId,
            option_type: optionType,
          }).unwrap();
          if (created.length > 0) {
            setHistoryNotice({
              status: "success",
              message: "Account history was created successfully.",
            });
          }
        } catch (e) {
          setHistoryNotice({
            status: "error",
            message: extractReportErrorMessage(
              e,
              "Couldn't create account history. Please check the settings.",
            ),
          });
          return null;
        }
      }

      setIsCalculating(true);
      try {
        const started = await startNewReportJob(args).unwrap();
        const job = await pollReportJob(started, (key) =>
          fetchJobStatus({ key }).unwrap(),
        );
        if (job.status !== ReportJobStatus.COMPLETED || !job.result) {
          setError(reportJobErrorMessage(job, fallbackErrorMessage));
          return null;
        }
        return job.result.data;
      } catch (e) {
        setError(extractReportErrorMessage(e, fallbackErrorMessage));
        return null;
      } finally {
        setIsCalculating(false);
      }
    },
    [
      startNewReportJob,
      fetchJobStatus,
      ensureHistory,
      hasBoth,
      fallbackErrorMessage,
      assetId,
      optionType,
    ],
  );

  return {
    calculate,
    error,
    setError,
    historyNotice,
    setHistoryNotice,
    isCalculating,
    isEnsuringHistory,
    assetId,
    optionType,
  };
};
